import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Resizable Image Extension for TipTap
 * Allows users to resize inline images by dragging corner handles
 */
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { useCallback, useRef, useState } from 'react';
const ResizableImageComponent = ({ node, updateAttributes, selected, }) => {
    const containerRef = useRef(null);
    const imgRef = useRef(null);
    const [isResizing, setIsResizing] = useState(false);
    const [aspectRatio, setAspectRatio] = useState(null);
    const startPosRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
    const { src, alt, title, width, height } = node.attrs;
    // Calculate aspect ratio when image loads
    const handleImageLoad = useCallback(() => {
        if (imgRef.current && !aspectRatio) {
            const { naturalWidth, naturalHeight } = imgRef.current;
            if (naturalWidth && naturalHeight) {
                setAspectRatio(naturalWidth / naturalHeight);
            }
        }
    }, [aspectRatio]);
    const handleMouseDown = useCallback((e, corner) => {
        e.preventDefault();
        e.stopPropagation();
        if (!imgRef.current)
            return;
        const rect = imgRef.current.getBoundingClientRect();
        startPosRef.current = {
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
        };
        setIsResizing(true);
        const handleMouseMove = (moveEvent) => {
            const deltaX = moveEvent.clientX - startPosRef.current.x;
            const deltaY = moveEvent.clientY - startPosRef.current.y;
            let newWidth = startPosRef.current.width;
            let newHeight = startPosRef.current.height;
            // Calculate new dimensions based on corner
            if (corner.includes('e')) {
                newWidth = Math.max(50, startPosRef.current.width + deltaX);
            }
            if (corner.includes('w')) {
                newWidth = Math.max(50, startPosRef.current.width - deltaX);
            }
            if (corner.includes('s')) {
                newHeight = Math.max(50, startPosRef.current.height + deltaY);
            }
            if (corner.includes('n')) {
                newHeight = Math.max(50, startPosRef.current.height - deltaY);
            }
            // Maintain aspect ratio if shift is held or for corner handles
            if (aspectRatio && (corner === 'se' || corner === 'sw' || corner === 'ne' || corner === 'nw')) {
                // Use width as primary and calculate height
                newHeight = newWidth / aspectRatio;
            }
            updateAttributes({
                width: Math.round(newWidth),
                height: Math.round(newHeight),
            });
        };
        const handleMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [aspectRatio, updateAttributes]);
    // Parse width/height - could be number or string with 'px'
    const parseSize = (size) => {
        if (size === undefined || size === null)
            return undefined;
        if (typeof size === 'number')
            return size;
        const parsed = parseInt(size, 10);
        return isNaN(parsed) ? undefined : parsed;
    };
    const parsedWidth = parseSize(width);
    const parsedHeight = parseSize(height);
    return (_jsx(NodeViewWrapper, { className: "relative inline-block", children: _jsxs("div", { ref: containerRef, className: `relative inline-block ${selected ? 'ring-2 ring-primary ring-offset-2' : ''}`, style: { lineHeight: 0 }, children: [_jsx("img", { ref: imgRef, src: src, alt: alt || '', title: title || '', onLoad: handleImageLoad, className: "max-w-full h-auto rounded-lg", style: {
                        width: parsedWidth ? `${parsedWidth}px` : undefined,
                        height: parsedHeight ? `${parsedHeight}px` : undefined,
                        display: 'block',
                    }, draggable: false }), selected && (_jsxs(_Fragment, { children: [_jsx("div", { className: "absolute -top-1 -left-1 w-3 h-3 bg-primary border-2 border-white rounded-sm cursor-nw-resize shadow-sm", onMouseDown: (e) => handleMouseDown(e, 'nw') }), _jsx("div", { className: "absolute -top-1 -right-1 w-3 h-3 bg-primary border-2 border-white rounded-sm cursor-ne-resize shadow-sm", onMouseDown: (e) => handleMouseDown(e, 'ne') }), _jsx("div", { className: "absolute -bottom-1 -left-1 w-3 h-3 bg-primary border-2 border-white rounded-sm cursor-sw-resize shadow-sm", onMouseDown: (e) => handleMouseDown(e, 'sw') }), _jsx("div", { className: "absolute -bottom-1 -right-1 w-3 h-3 bg-primary border-2 border-white rounded-sm cursor-se-resize shadow-sm", onMouseDown: (e) => handleMouseDown(e, 'se') }), _jsx("div", { className: "absolute top-1/2 -left-1 w-2 h-6 -translate-y-1/2 bg-primary border-2 border-white rounded-sm cursor-w-resize shadow-sm", onMouseDown: (e) => handleMouseDown(e, 'w') }), _jsx("div", { className: "absolute top-1/2 -right-1 w-2 h-6 -translate-y-1/2 bg-primary border-2 border-white rounded-sm cursor-e-resize shadow-sm", onMouseDown: (e) => handleMouseDown(e, 'e') }), _jsx("div", { className: "absolute -top-1 left-1/2 w-6 h-2 -translate-x-1/2 bg-primary border-2 border-white rounded-sm cursor-n-resize shadow-sm", onMouseDown: (e) => handleMouseDown(e, 'n') }), _jsx("div", { className: "absolute -bottom-1 left-1/2 w-6 h-2 -translate-x-1/2 bg-primary border-2 border-white rounded-sm cursor-s-resize shadow-sm", onMouseDown: (e) => handleMouseDown(e, 's') }), parsedWidth && parsedHeight && (_jsxs("div", { className: "absolute -bottom-6 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-black/70 text-white text-xs rounded whitespace-nowrap", children: [parsedWidth, " \u00D7 ", parsedHeight] }))] })), isResizing && (_jsx("div", { className: "absolute inset-0 bg-primary/10 pointer-events-none" }))] }) }));
};
export const ResizableImage = Node.create({
    name: 'image',
    group: 'inline',
    inline: true,
    draggable: true,
    addAttributes() {
        return {
            src: {
                default: null,
            },
            alt: {
                default: null,
            },
            title: {
                default: null,
            },
            width: {
                default: null,
                parseHTML: (element) => element.getAttribute('width'),
                renderHTML: (attributes) => {
                    if (!attributes.width)
                        return {};
                    return { width: attributes.width };
                },
            },
            height: {
                default: null,
                parseHTML: (element) => element.getAttribute('height'),
                renderHTML: (attributes) => {
                    if (!attributes.height)
                        return {};
                    return { height: attributes.height };
                },
            },
        };
    },
    parseHTML() {
        return [
            {
                tag: 'img[src]',
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['img', mergeAttributes(HTMLAttributes)];
    },
    addNodeView() {
        return ReactNodeViewRenderer(ResizableImageComponent);
    },
    addCommands() {
        return {
            setImage: (options) => ({ commands }) => {
                return commands.insertContent({
                    type: this.name,
                    attrs: options,
                });
            },
        };
    },
});
export default ResizableImage;
//# sourceMappingURL=ResizableImage.js.map